import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { apiClient } from '../services/api';
import type { NftPurchaseItem } from '../types';
import './TransactionHistoryPage.css';

type SortOrder = 'desc' | 'asc';

interface MonthGroup {
  key: string;
  label: string;
  items: NftPurchaseItem[];
}

function groupByMonth(transactions: NftPurchaseItem[]): MonthGroup[] {
  const groups: Map<string, NftPurchaseItem[]> = new Map();

  for (const tx of transactions) {
    const date = new Date(tx.date);
    const key = `${date.getFullYear()}-${String(date.getMonth()).padStart(2, '0')}`;

    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(tx);
  }

  return Array.from(groups.entries()).map(([key, items]) => {
    const date = new Date(items[0].date);
    const raw = date.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
    return { key, label: raw.charAt(0).toUpperCase() + raw.slice(1), items };
  });
}

function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleDateString('ru-RU', { month: 'long' });
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day} ${month}, ${hours}:${minutes}`;
}

function formatAmount(amount: number): string {
  return amount.toLocaleString('ru-RU') + ' ₽';
}

export const TransactionHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState<NftPurchaseItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [pendingSortOrder, setPendingSortOrder] = useState<SortOrder>('desc');
  const [showSortModal, setShowSortModal] = useState(false);

  const loadTransactions = useCallback(async (page: number = 1, append: boolean = false, sort: SortOrder = sortOrder) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const response = await apiClient.getMyPurchases(page, sort);

      if (append) {
        setTransactions((prev) => [...prev, ...response.data]);
      } else {
        setTransactions(response.data);
      }

      setCurrentPage(response.pagination.current_page);
      setLastPage(response.pagination.last_page);
    } catch (err) {
      if (
        isAxiosError(err) &&
        err.response?.status === 412 &&
        err.response.data?.error === 'user_unverified'
      ) {
        if (!append) setTransactions([]);
        setCurrentPage(1);
        setLastPage(1);
        return;
      }
      console.error('Failed to load transactions:', err);
      setError('Не удалось загрузить список транзакций');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [sortOrder]);

  useEffect(() => {
    loadTransactions(1, false, sortOrder);
  }, [loadTransactions, sortOrder]);

  const handleLoadMore = () => {
    if (currentPage < lastPage && !loadingMore) {
      loadTransactions(currentPage + 1, true);
    }
  };

  const handleOpenSort = () => {
    setPendingSortOrder(sortOrder);
    setShowSortModal(true);
  };

  const handleApplySort = () => {
    setShowSortModal(false);
    if (pendingSortOrder !== sortOrder) {
      setSortOrder(pendingSortOrder);
    }
  };

  const handleCancelSort = () => {
    setShowSortModal(false);
  };

  const monthGroups = groupByMonth(transactions);

  if (loading) {
    return (
      <div className="page-container transactions-page">
        <div className="transactions-header">
          <button className="transactions-back-btn" onClick={() => navigate(-1)} aria-label="Назад">
            <img src="/chevron-back.png" alt="" width={24} height={24} />
          </button>
          <h1 className="transactions-header-title">История транзакций</h1>
          <div className="transactions-header-spacer" />
        </div>
        <div className="transactions-loading">Загрузка транзакций...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container transactions-page">
        <div className="transactions-header">
          <button className="transactions-back-btn" onClick={() => navigate(-1)} aria-label="Назад">
            <img src="/chevron-back.png" alt="" width={24} height={24} />
          </button>
          <h1 className="transactions-header-title">История транзакций</h1>
          <div className="transactions-header-spacer" />
        </div>
        <div className="transactions-error">
          <span className="transactions-error-text">{error}</span>
          <button className="transactions-retry-button button--accent" onClick={() => loadTransactions()}>
            Повторить
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container transactions-page">
      <div className="transactions-header">
        <button className="transactions-back-btn" onClick={() => navigate(-1)} aria-label="Назад">
          <img src="/chevron-back.png" alt="" width={24} height={24} />
        </button>
        <h1 className="transactions-header-title">История транзакций</h1>
        <button className="transactions-sort-btn" onClick={handleOpenSort} aria-label="Сортировка">
          <img src="/sort.png" alt="" width={22} height={22} />
          {sortOrder !== 'desc' && <span className="transactions-sort-active-dot" aria-hidden />}
        </button>
      </div>

      {transactions.length === 0 ? (
        <div className="transactions-empty">
          <span className="transactions-empty-title">Пока нет транзакций</span>
          <span className="transactions-empty-text">
            Здесь будут отображаться ваши покупки и продажи NFT
          </span>
        </div>
      ) : (
        <>
          {monthGroups.map((group) => (
            <div key={group.key} className="transactions-month-group">
              <div className="transactions-month-label">{group.label}</div>
              <div className="transactions-list">
                {group.items.map((tx) => (
                  <div key={tx.id} className="transaction-item">
                    <div className="transaction-nft-image">
                      {tx.nft?.image_url || tx.nft?.preview_image_url ? (
                        <img src={tx.nft.preview_image_url ?? tx.nft.image_url} alt={tx.nft.title} />
                      ) : (
                        <div className="transaction-nft-placeholder">
                          {tx.type === 'sale' ? '💰' : '🛒'}
                        </div>
                      )}
                    </div>
                    <div className="transaction-info">
                      <div className="transaction-nft-title">{tx.nft?.title ?? 'NFT'}</div>
                      {tx.counterparty && (
                        <div className="transaction-username">
                          {tx.counterparty.username ? `@${tx.counterparty.username}` : tx.counterparty.name}
                        </div>
                      )}
                      <div className="transaction-date">{formatDateTime(tx.date)}</div>
                    </div>
                    <div className="transaction-right">
                      <span className={`transaction-type-badge ${tx.type}`}>
                        {tx.type === 'sale' ? '↑ Продажа' : '↓ Покупка'}
                      </span>
                      <span className="transaction-amount">{formatAmount(tx.amount)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {currentPage < lastPage && (
            <button
              className="transactions-load-more button--secondary button--full"
              onClick={handleLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? 'Загрузка...' : 'Показать ещё'}
            </button>
          )}
        </>
      )}

      {showSortModal && (
        <div className="sort-overlay" onClick={handleCancelSort}>
          <div className="sort-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="sort-sheet-header">
              <span className="sort-sheet-title">Показать сначала</span>
              <button className="sort-sheet-close" onClick={handleCancelSort} aria-label="Закрыть">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            <div className="sort-sheet-options">
              <button
                className={`sort-sheet-option ${pendingSortOrder === 'desc' ? 'active' : ''}`}
                onClick={() => setPendingSortOrder('desc')}
              >
                <span>Новые</span>
                {pendingSortOrder === 'desc' && (
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M4 10L8 14L16 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </button>
              <button
                className={`sort-sheet-option ${pendingSortOrder === 'asc' ? 'active' : ''}`}
                onClick={() => setPendingSortOrder('asc')}
              >
                <span>Старые</span>
                {pendingSortOrder === 'asc' && (
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M4 10L8 14L16 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </button>
            </div>
            <div className="sort-sheet-actions">
              <button className="button--primary button--full" onClick={handleApplySort}>Применить</button>
              <button className="button--secondary button--full" onClick={handleCancelSort}>Отмена</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
